
import { CheckCircle2 } from 'lucide-react';
import type { useForm } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { PALETTE_FOREST, PALETTE_MINT } from '../tokens';
import type { QuestionnaireInput } from '../schema';

interface CheckboxGroupProps {
  label:   string;
  hint?:   string;
  /** Controlled form field name. Expanded as the questionnaire grows. */
  name:
    | 'income_exposures'
    | 'services_required';
  options: ReadonlyArray<{ value: string; label: string }>;
  control: ReturnType<typeof useForm<QuestionnaireInput>>['control'];
  error?:  string;
  /** When true, the option with value 'none' is mutually exclusive with all others. */
  noneExclusive?: boolean;
}

export function CheckboxGroup({
  label, hint, name, options, control, error, noneExclusive,
}: CheckboxGroupProps) {
  return (
    <div>
      <div className="mb-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {label}
        </p>
        {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
      </div>

      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const values = (field.value ?? []) as string[];

          function toggle(val: string) {
            const isOn = values.includes(val);

            if (noneExclusive) {
              // Picking 'none' wipes everything else; clicking it again clears.
              if (val === 'none') {
                field.onChange(isOn ? [] : ['none']);
                return;
              }
              // Picking any non-none option clears 'none' first.
              const cleaned = values.filter((v) => v !== 'none');
              if (isOn) {
                field.onChange(cleaned.filter((v) => v !== val));
              } else {
                field.onChange([...cleaned, val]);
              }
              return;
            }

            // Default — plain toggle.
            if (isOn) field.onChange(values.filter((v) => v !== val));
            else      field.onChange([...values, val]);
          }

          return (
            <div className="flex flex-wrap gap-2">
              {options.map((opt) => {
                const checked = values.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggle(opt.value)}
                    className="inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-sm font-medium transition-all focus:outline-none"
                    style={{
                      borderColor:     checked ? PALETTE_FOREST : 'hsl(var(--border))',
                      backgroundColor: checked ? PALETTE_MINT   : 'transparent',
                      color:           checked ? PALETTE_FOREST : 'hsl(var(--foreground))',
                    }}
                  >
                    {checked && <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          );
        }}
      />
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}
